import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Generates an array of animation delays starting from an initial value
 * and incrementing by a specified amount for each subsequent delay.
 *
 * @param count
 * @param increment
 * @param initialValue
 * @returns
 *
 * @example
 * // Generate 4 delays starting from 0, incrementing by 200: [0, 200, 400, 600]
 * const delays = generateAnimationDelays(4, 200, 0);
 */
export const generateAnimationDelays = (
  count: number,
  increment: number = 300,
  initialValue: number = 0
): readonly number[] => {
  if (count < 1) {
    return [];
  }

  const delays: number[] = [];
  for (let i = 0; i < count; i++) {
    delays.push(initialValue + i * increment);
  }

  return delays as readonly number[];
};

export interface UseStaggeredAnimationOptions {
  delays: readonly number[] | number[];
  threshold?: number;
  rootMargin?: string;
}

const MIN_VARIATIONS = 1;
const MAX_VARIATIONS = 20;

export const useStaggeredAnimation = ({
  delays,
  threshold = 0.1,
  rootMargin,
}: UseStaggeredAnimationOptions) => {
  if (delays.length < MIN_VARIATIONS || delays.length > MAX_VARIATIONS) {
    throw new Error(
      `useStaggeredAnimation requires between ${MIN_VARIATIONS} and ${MAX_VARIATIONS} delays, received ${delays.length}`
    );
  }

  const [animatedStates, setAnimatedStates] = useState<boolean[]>(
    new Array(delays.length).fill(false)
  );
  const sectionRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const timeoutRefs = useRef<Array<number | NodeJS.Timeout>>([]);
  const hasAnimatedRef = useRef(false);

  const cleanupTimeouts = useCallback(() => {
    timeoutRefs.current.forEach((timeout) => {
      if (typeof timeout === "number") {
        cancelAnimationFrame(timeout);
      } else {
        clearTimeout(timeout);
      }
    });
    timeoutRefs.current = [];
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || hasAnimatedRef.current) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimatedRef.current) {
          hasAnimatedRef.current = true;

          const scheduleAnimation = (index: number, delay: number) => {
            const setter = () => {
              setAnimatedStates((prev) => {
                const newStates = [...prev];
                newStates[index] = true;
                return newStates;
              });
            };

            if (delay === 0) {
              const rafId = requestAnimationFrame(() => {
                setter();
              });
              timeoutRefs.current.push(rafId);
            } else {
              const timeoutId = setTimeout(() => {
                const rafId = requestAnimationFrame(() => {
                  setter();
                });
                timeoutRefs.current.push(rafId);
              }, delay);
              timeoutRefs.current.push(timeoutId);
            }
          };

          delays.forEach((delay, index) => {
            scheduleAnimation(index, delay);
          });

          observer.disconnect();
        }
      },
      {
        threshold,
        ...(rootMargin && { rootMargin }),
      }
    );

    observer.observe(el);
    observerRef.current = observer;

    return () => {
      cleanupTimeouts();
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [delays, threshold, rootMargin, cleanupTimeouts]);

  return {
    sectionRef,
    animatedStates,
  };
};
